import { Actions, Subjects } from '@boqnet/permissions';
import { useCan } from '@/composables/use-can';
import { ROLE_CONSTANTS, useRolesStore } from '@/stores/roles.store';
import { useFeatureFlagsStore } from '@/stores/feature-flags.store';
import { hasOrgMembRoleWithNoOtherRoles } from '@/common/utils/role.utils';
import { BOQRoles, OrgRoles, SiteRoles } from '@/types/api/roles.types';

/** A project, a site, or both — whichever the caller has in context. */
type EntityScope = { projectId?: string; siteId?: string };

/**
 * Who a user *is* — never what they may do.
 *
 * Context: these questions look like permission checks and are not. They classify the user so
 * the app can route them, shape navigation, decide which of two layouts to show, or choose
 * which data to prefetch; none of them answers "may this person perform action X on subject Y".
 * That is why they are deliberately excluded from the CASL migration and from the ESLint fence
 * that drives it: a fence which flags them alongside real gates can never reach zero, because
 * these are permanent (PLE-1236 C2, ratified as decision D1 on 8 September 2026).
 *
 * Any check that decides whether a control is available belongs in `can(action, subject)` via
 * `useCan()` or `<PermissionGate>` — not here. If you find yourself reaching for this module to
 * hide a button, the answer is a subject in `@boqnet/permissions`.
 */
export const userIdentity = {
  /**
   * Holds only client and/or professional roles — an outside collaborator rather than staff.
   * BOQ platform roles are internal and never external-only, even in an org where they hold
   * nothing else.
   */
  isExternalOnly(): boolean {
    return useRolesStore().isExternalUserOnly;
  },

  /**
   * A true bare member: `ORG_MEMB` is the user's only org role, with no project or site roles
   * anywhere. Such a user has no standalone org surface and is routed to an empty state.
   *
   * Any other org role — finance officer, finance admin, admin, owner — grants org access on
   * its own, so a holder is *not* a bare member.
   */
  isBareOrgMember(): boolean {
    return hasOrgMembRoleWithNoOtherRoles();
  },

  /** Runs operations on this project — its manager or its procurement officer. */
  runsProjectOperations(projectId: string): boolean {
    return useRolesStore().hasProjectOperationsRole(projectId);
  },

  /** Runs operations on *any* project, for a control with no project in context. */
  runsAnyProjectOperations(): boolean {
    return useRolesStore().hasOrgProjectOperationsRole();
  },

  /**
   * BOQ platform staff — internal, cross-org users. Held independently of any organisation, so
   * a platform admin in an org where they hold nothing else is still staff.
   */
  isPlatformStaff(): boolean {
    return useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]);
  },

  /** An org admin or owner. */
  isOrgAdministrator(): boolean {
    return useRolesStore().permissions.organisation.hasRoleAnywhereInOrganisation([
      ...ROLE_CONSTANTS.ADMIN_ROLES,
    ]);
  },

  /**
   * Delivery staff: holds a project or site operations role, or administers the org. Excludes
   * the org finance tier, who oversee rather than run the work.
   */
  runsOrgOperations(): boolean {
    return useRolesStore().permissions.organisation.hasRoleAnywhereInOrganisation([
      ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
      ...ROLE_CONSTANTS.ADMIN_ROLES,
    ]);
  },

  /**
   * `runsOrgOperations` plus the org finance tier — the audience for the staff navigation and
   * the badge counts it carries.
   */
  seesStaffNavigation(): boolean {
    return useRolesStore().permissions.organisation.hasRoleAnywhereInOrganisation([
      ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
      ...ROLE_CONSTANTS.ADMIN_ROLES,
      OrgRoles.ORG_FO,
      OrgRoles.ORG_FA,
    ]);
  },

  /** Runs site-level operations somewhere in the org, for a control with no site in context. */
  runsAnySiteOperations(): boolean {
    return useRolesStore().permissions.organisation.hasRoleAnywhereInOrganisation([
      SiteRoles.SITE_ADMIN,
      SiteRoles.SITE_FIN_OFF,
      SiteRoles.SITE_FLD_USR,
      SiteRoles.SITE_PROC_OFF,
    ]);
  },

  /** Holds a client role — base or approver — anywhere in the org. */
  isClientAnywhere(): boolean {
    return useRolesStore().permissions.organisation.hasRoleAnywhereInOrganisation([
      ...ROLE_CONSTANTS.CLIENT_ROLES,
    ]);
  },

  /** An outside professional on either entity passed. */
  isProfessionalOn({ projectId, siteId }: EntityScope): boolean {
    const { permissions } = useRolesStore();

    return (
      (projectId ? permissions.project.isProfessional(projectId) : false) ||
      (siteId ? permissions.site.isProfessional(siteId) : false)
    );
  },

  /** A client — base or approver — on either entity passed. */
  isClientOn({ projectId, siteId }: EntityScope): boolean {
    const { permissions } = useRolesStore();

    return (
      (projectId
        ? permissions.project.isClient(projectId) ||
          permissions.project.isClientWithApproval(projectId)
        : false) ||
      (siteId
        ? permissions.site.isClient(siteId) || permissions.site.isClientWithApproval(siteId)
        : false)
    );
  },

  /** Sees the projects and sites navigation, and the data behind it. */
  seesProjectNavigation(): boolean {
    return useRolesStore().permissions.navigation.canSeeProjects();
  },

  /**
   * Sees the org settings menu. Deliberately omits the BOQ-staff arm the other navigation
   * audiences carry, preserving the historical per-layout settings-menu behaviour.
   */
  seesSettingsMenu(): boolean {
    return useRolesStore().permissions.navigation.canSeeSettings();
  },

  /**
   * Can use the BOQ database and nothing else in the org, like a catalog admin with no other role.
   * MANAGE:BOQ_VENDOR is what the BOQ database route guards on, so redirecting there cannot loop.
   */
  worksOnBoqDatabaseOnly(): boolean {
    const { can } = useCan();
    return (
      useFeatureFlagsStore().boqCatalogAdminEnabled &&
      can(Actions.MANAGE, Subjects.BOQ_VENDOR) &&
      !can(Actions.READ, Subjects.PROJECT) &&
      !can(Actions.READ, Subjects.CLIENT_DASHBOARD)
    );
  },
} as const;
